// frontend/src/pages/Profile/components/GoogleDriveIntegration.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Stack,
  Text,
  Button,
  Group,
  Alert,
  Loader,
  Progress,
  Divider,
  Center,
  Spoiler,
  List,
} from '@mantine/core';
import {
  IconBrandGoogleDrive,
  IconCheck,
  IconAlertCircle,
  IconLogout,
  IconMail,
  IconCloudUpload,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { integrationsApi, GoogleDriveStatus, BulkExportProgress } from '../../../api/integrations.api';
import { notifications } from '@mantine/notifications';

interface Props {
  onClose: () => void;
}

const GoogleDriveIntegration: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [status, setStatus] = useState<GoogleDriveStatus | null>(null);
  const [exportProgress, setExportProgress] = useState<BulkExportProgress | null>(null);
  const [startingExport, setStartingExport] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadStatus();
    checkExportProgress();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Check URL params for OAuth callback result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gdriveResult = params.get('gdrive');

    if (gdriveResult === 'success') {
      notifications.show({
        title: t('integrations.googleDrive.title'),
        message: t('integrations.googleDrive.connectSuccess'),
        color: 'green',
        icon: <IconCheck size={18} />,
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('gdrive');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
      loadStatus();
    } else if (gdriveResult === 'error') {
      notifications.show({
        title: t('integrations.googleDrive.title'),
        message: t('integrations.googleDrive.connectError'),
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('gdrive');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await integrationsApi.googleDriveStatus();
      if (response.data.success) {
        setStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error loading Google Drive status:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExportProgress = async () => {
    try {
      const response = await integrationsApi.googleDriveBulkExportProgress();
      if (response.data.success) {
        const progress = response.data.data;
        setExportProgress(progress);
        if (progress.running && !pollRef.current) {
          startPolling();
        }
      }
    } catch {
      // ignore
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const response = await integrationsApi.googleDriveBulkExportProgress();
        if (response.data.success) {
          const progress = response.data.data;
          setExportProgress(progress);
          if (!progress.running && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch {
        // ignore
      }
    }, 3000);
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const response = await integrationsApi.googleDriveConnect();
      if (response.data.success && response.data.data.authUrl) {
        window.location.href = response.data.data.authUrl;
      }
    } catch (error) {
      console.error('Error connecting Google Drive:', error);
      notifications.show({
        title: t('common.error'),
        message: t('integrations.googleDrive.connectError'),
        color: 'red',
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await integrationsApi.googleDriveDisconnect();
      setStatus({ connected: false });
      notifications.show({
        title: t('integrations.googleDrive.title'),
        message: t('integrations.googleDrive.disconnected'),
        color: 'blue',
      });
    } catch (error) {
      console.error('Error disconnecting Google Drive:', error);
      notifications.show({
        title: t('common.error'),
        message: t('integrations.googleDrive.disconnectError'),
        color: 'red',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleBulkExport = async () => {
    try {
      setStartingExport(true);
      const response = await integrationsApi.googleDriveBulkExport();
      if (response.data.success) {
        notifications.show({
          title: t('integrations.googleDrive.title'),
          message: t('integrations.googleDrive.bulkExportStarted'),
          color: 'blue',
        });
        // Start polling
        setTimeout(() => checkExportProgress(), 1000);
        startPolling();
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || t('integrations.googleDrive.bulkExportError');
      notifications.show({
        title: t('common.error'),
        message: msg,
        color: 'red',
      });
    } finally {
      setStartingExport(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatElapsed = (startedAt: string): string => {
    const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (loading) {
    return (
      <Center py={40}>
        <Loader size="md" />
      </Center>
    );
  }

  if (status?.connected) {
    const storagePercent = status.storage
      ? Math.round((status.storage.usage / status.storage.limit) * 100)
      : 0;

    const isExporting = exportProgress?.running;
    const exportDone = exportProgress?.completedAt && !exportProgress?.running;
    const filePercent = exportProgress && exportProgress.totalFiles > 0
      ? Math.round((exportProgress.uploadedFiles / exportProgress.totalFiles) * 100)
      : 0;

    return (
      <Stack gap="lg">
        <Alert color="green" variant="light" icon={<IconCheck size={20} />}>
          {t('integrations.googleDrive.connectedAs', { email: status.email })}
        </Alert>

        <Stack gap="xs">
          <Group gap="xs">
            <IconMail size={16} />
            <Text size="sm" fw={500}>{status.email}</Text>
          </Group>
          {status.name && (
            <Text size="sm" c="dimmed">{status.name}</Text>
          )}
        </Stack>

        {status.storage && (
          <>
            <Divider />
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500}>{t('integrations.googleDrive.storage')}</Text>
                <Text size="sm" c="dimmed">
                  {formatBytes(status.storage.usage)} / {formatBytes(status.storage.limit)}
                </Text>
              </Group>
              <Progress
                value={storagePercent}
                color={storagePercent > 90 ? 'red' : storagePercent > 70 ? 'yellow' : 'blue'}
                size="sm"
                radius="xl"
              />
            </Stack>
          </>
        )}

        <Divider />

        {/* Bulk Export Section */}
        <Stack gap="md">
          <Text size="sm" fw={500}>{t('integrations.googleDrive.bulkExport')}</Text>
          <Text size="sm" c="dimmed">
            {t('integrations.googleDrive.bulkExportDescription')}
          </Text>

          {/* Export in progress */}
          {isExporting && exportProgress && (
            <Stack gap="xs">
              <Alert color="blue" variant="light" icon={<Loader size={16} />}>
                {t('integrations.googleDrive.bulkExportRunning')}
              </Alert>

              <Stack gap={4}>
                <Group justify="space-between">
                  <Text size="xs" fw={500}>
                    {exportProgress.currentProperty}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {exportProgress.completedProperties}/{exportProgress.totalProperties} {t('integrations.googleDrive.properties')}
                  </Text>
                </Group>
                <Progress value={filePercent} size="md" radius="xl" animated />
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">
                    {exportProgress.uploadedFiles}/{exportProgress.totalFiles} {t('integrations.googleDrive.files')}
                  </Text>
                  {exportProgress.startedAt && (
                    <Text size="xs" c="dimmed">
                      {formatElapsed(exportProgress.startedAt)}
                    </Text>
                  )}
                </Group>
              </Stack>

              {exportProgress.errors.length > 0 && (
                <Text size="xs" c="red">
                  {exportProgress.errors.length} {t('integrations.googleDrive.errorsCount')}
                </Text>
              )}
            </Stack>
          )}

          {/* Export completed */}
          {exportDone && exportProgress && (
            <Stack gap="xs">
              <Alert
                color={exportProgress.errors.length > 0 ? 'yellow' : 'green'}
                variant="light"
                icon={<IconCheck size={16} />}
              >
                {t('integrations.googleDrive.bulkExportComplete', {
                  count: exportProgress.completedProperties,
                  total: exportProgress.totalProperties,
                  files: exportProgress.uploadedFiles,
                })}
              </Alert>

              {exportProgress.errors.length > 0 && (
                <Spoiler maxHeight={0} showLabel={`${t('integrations.googleDrive.showErrors')} (${exportProgress.errors.length})`} hideLabel={t('integrations.googleDrive.hideErrors')}>
                  <List size="xs" c="red" mt="xs">
                    {exportProgress.errors.map((err, i) => (
                      <List.Item key={i}>
                        <Text size="xs">{err.property}{err.file ? ` / ${err.file}` : ''}: {err.error}</Text>
                      </List.Item>
                    ))}
                  </List>
                </Spoiler>
              )}
            </Stack>
          )}

          {/* Export button */}
          {!isExporting && (
            <Button
              leftSection={<IconCloudUpload size={18} />}
              onClick={handleBulkExport}
              loading={startingExport}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan' }}
              fullWidth
            >
              {t('integrations.googleDrive.bulkExportButton')}
            </Button>
          )}
        </Stack>

        <Divider />

        <Group justify="flex-end">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconLogout size={16} />}
            onClick={handleDisconnect}
            loading={disconnecting}
            disabled={!!isExporting}
          >
            {t('integrations.googleDrive.disconnect')}
          </Button>
          <Button onClick={onClose}>
            {t('common.close')}
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        {t('integrations.googleDrive.description')}
      </Text>

      <Alert color="blue" variant="light" icon={<IconBrandGoogleDrive size={20} />}>
        {t('integrations.googleDrive.connectInfo')}
      </Alert>

      <Stack gap="xs">
        <Text size="sm" fw={500}>{t('integrations.googleDrive.features')}</Text>
        <Text size="sm" c="dimmed">{t('integrations.googleDrive.feature1')}</Text>
        <Text size="sm" c="dimmed">{t('integrations.googleDrive.feature2')}</Text>
        <Text size="sm" c="dimmed">{t('integrations.googleDrive.feature3')}</Text>
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          leftSection={<IconBrandGoogleDrive size={18} />}
          onClick={handleConnect}
          loading={connecting}
          variant="gradient"
          gradient={{ from: 'blue', to: 'cyan' }}
        >
          {t('integrations.googleDrive.connect')}
        </Button>
      </Group>
    </Stack>
  );
};

export default GoogleDriveIntegration;
